import React, { useEffect, useState } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Card, Col, Row, Table, Dropdown } from "react-bootstrap";
import { Link } from "react-router-dom";
import { getToken, getUserId } from '../services/authorize'
import axiosInstance from "../services/axiosInterceptor"
import Swal from "sweetalert2";
import { useLoading } from '../contexts/LoadingContext';
import { getDayNameTHByIndex, groupSubjectData, formatTimeShort, getSemesterOrder } from "../utils/teachingUtils";

// รายละเอียดคาบสอน ใช้ทั้งในตารางและการ์ด
const TimesDisplay = ({ times }) => (
  times && times.length > 0
    ? times.map((t, i) => (
        <div key={i}>
          {t.period_order
            ? <>{`วัน${getDayNameTHByIndex(t.teach_day)} คาบ ${t.period_order}`}<br />{`(${formatTimeShort(t.period_begin)} - ${formatTimeShort(t.period_end)})`}</>
            : "ยังไม่ระบุ"}
        </div>
      ))
    : "-"
)

// การ์ดรายวิชา 1 รายการ พร้อมปุ่มบันทึกผลการเรียน สำหรับมุมมองมือถือ
const SubjectGradeCard = ({ item }) => (
  <div className="border rounded p-3">
    <div className="fw-semibold">{item.sub_subid} {item.sub_subname}</div>
    <div className="fs-xs text-muted">ชั้น {item.tb_class_c_id}/{item.r_no} · ภาคเรียนที่ {item.sub_semester}</div>
    <div className="mt-2 pt-2 border-top fs-sm">
      <div><span className="text-muted">คาบ:</span> <TimesDisplay times={item.times} /></div>
      <div><span className="text-muted">อาจารย์ที่ปรึกษา:</span> {item.advisor_name}</div>
    </div>
    <div className="mt-2 pt-2 border-top d-flex flex-column gap-2">
      <Link to={`/teaching/score-table/${item.teach_id}`} className="btn btn-outline-success btn-sm">
        <i className="ri-table-line"></i> ตารางคะแนน
      </Link>
      <Link to={`/teaching/grade-entry/${item.teach_id}`} className="btn btn-primary btn-sm">
        <i className="ri-edit-box-line"></i> บันทึกผลการเรียน
      </Link>
      <Dropdown>
        <Dropdown.Toggle variant="outline-secondary" size="sm" className="w-100" id={`dropdown-por5-card-${item.teach_id}`}>
          ปพ.5
        </Dropdown.Toggle>
        <Dropdown.Menu>
          <Dropdown.Item as={Link} to={`/teaching/por5-assessment/${item.teach_id}`}>
            <i className="ri-survey-line me-1"></i>ประเมินคุณลักษณะฯ / อ่านคิดวิเคราะห์
          </Dropdown.Item>
          <Dropdown.Item as={Link} to={`/teaching/por5-book/${item.teach_id}`}>
            <i className="ri-printer-line me-1"></i>พิมพ์เล่ม ปพ.5
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
    </div>
  </div>
)

export default function SaveGrade() {
  const { setLoading } = useLoading();
  const currentSkin = (localStorage.getItem('skin-mode')) ? 'dark' : '';
  const [skin, setSkin] = useState(currentSkin);

  const switchSkin = (skin) => {
    if (skin === 'dark') {
      const btnWhite = document.getElementsByClassName('btn-white');
      for (const btn of btnWhite) {
        btn.classList.add('btn-outline-primary');
        btn.classList.remove('btn-white');
      }
    } else {
      const btnOutlinePrimary = document.getElementsByClassName('btn-outline-primary');
      for (const btn of btnOutlinePrimary) {
        btn.classList.remove('btn-outline-primary');
        btn.classList.add('btn-white');
      }
    }
    const tables = document.getElementsByTagName('table');
    for (const table of tables) {
      const theads = table.querySelectorAll('thead');
      if (skin === 'dark') {
        for (const thead of theads) {
          thead.classList.remove('table-secondary');
          thead.classList.add('table-primary');
        }
        table.classList.add('table-dark');
      } else {
        table.classList.remove('table-dark');
        for (const thead of theads) {
          thead.classList.remove('table-primary');
          thead.classList.add('table-secondary');
        }
      }
    }
  }

  switchSkin(skin);

  useEffect(() => {
    switchSkin(skin);
  }, [skin]);

  const [data, setData] = useState([])
  const [yearId, setYearId] = useState(() => localStorage.getItem('yearId') || null)
  const [selectedYear, setSelectedYear] = useState("");
  const [yearList, setYearList] = useState([]);
  const [view, setView] = useState(() =>
    (typeof window !== 'undefined' && window.innerWidth < 768) ? 'card' : 'table')

  useEffect(() => {
    setLoading(true);
    axiosInstance.get(`${process.env.REACT_APP_API}/getStudyYear`, {
      headers: { authorization: `Bearer ${getToken()}` }
    })
    .then(res => {
      const list = res.data;
      setYearList(list);
      let initialYearId = localStorage.getItem('yearId');
      if (!initialYearId && list.length > 0) {
        initialYearId = list[0].year_id;
        localStorage.setItem('yearId', initialYearId);
      }
      setYearId(initialYearId);
      const selected = list.find(y => String(y.year_id) === String(initialYearId));
      setSelectedYear(selected ? `ปีการศึกษา ${selected.year_year}` : "");
    })
    .catch(() => {})
    .finally(() => setLoading(false));
  }, [setLoading]);

  useEffect(() => {
    if (yearId === null) return;
    setLoading(true);
    axiosInstance.get(`${process.env.REACT_APP_API}/getSubjectTeaching/${getUserId()}/${yearId}`, {
      headers: { authorization: `Bearer ${getToken()}` }
    })
    .then(response => {
      setData(groupSubjectData(response.data));
    })
    .catch(err => {
      Swal.fire({
        title: err.response?.data?.title || err.response?.statusText,
        text: err.response?.data?.message || err.message,
        icon: "error"
      });
      setData([]);
    })
    .finally(() => setLoading(false));
  }, [yearId, setLoading]);

  const handleSelectYear = (id) => {
    const selected = yearList.find(y => y.year_id === id);
    if (selected) {
      setYearId(id);
      setSelectedYear(`ปีการศึกษา ${selected.year_year}`);
      localStorage.setItem('yearId', id);
    }
  };

  // ถ้าตอนนี้อยู่เทอม 2 ให้แสดงภาคเรียนที่ 2 ก่อน
  const semesterOrder = getSemesterOrder();

  const subjectCardSection = (semester) => (
    <>
      <h6 className="text-center mb-3">ภาคเรียนที่ {semester}</h6>
      <Row className="g-3 mb-4">
        {data && data.length > 0 && data.some(d => d.sub_semester === String(semester)) ? (
          data.filter(d => d.sub_semester === String(semester)).map(item => (
            <Col key={item.teach_id} xs={12} md={6} xl={4}>
              <SubjectGradeCard item={item} />
            </Col>
          ))
        ) : (
          <Col xs={12}><div className="text-center text-muted py-3">ไม่พบข้อมูล</div></Col>
        )}
      </Row>
    </>
  );

  const subjectTableSection = (semester) => (
    <>
      <thead>
        <tr className="text-center">
          <th scope="row" colSpan={7}>ภาคเรียนที่ {semester}</th>
        </tr>
      </thead>
      <thead>
        <tr>
          <th scope="col" className="text-center" style={{ width: "70px" }}>รหัสวิชา</th>
          <th scope="col" className="text-center">ชื่อวิชา</th>
          <th scope="col" className="text-center" style={{ width: "70px" }}>ชั้น</th>
          <th scope="col" className="text-center" style={{ width: "150px" }}>คาบ</th>
          <th scope="col" className="text-center" style={{ width: "80px" }}>ภาคเรียน</th>
          <th scope="col" className="text-center">อาจารย์ที่ปรึกษา</th>
          <th scope="col" className="text-center" style={{ width: "160px" }}></th>
        </tr>
      </thead>
      <tbody>
        {data && data.length > 0 && data.some(d => d.sub_semester === String(semester)) ? (
          data
            .filter(d => d.sub_semester === String(semester))
            .map((item) => (
              <tr key={item.teach_id}>
                <th scope="row" className="text-center">{item.sub_subid}</th>
                <td className="text-center">{item.sub_subname}</td>
                <td className="text-center">{item.tb_class_c_id + "/" + item.r_no}</td>
                <td className="text-center">
                  {item.times && item.times.length > 0
                    ? item.times.map((t, i) =>
                        <div key={i}>
                          {t.period_order
                            ? <>
                                {`วัน${getDayNameTHByIndex(t.teach_day)} คาบ ${t.period_order}`}
                                <br />
                                {`(${formatTimeShort(t.period_begin)} - ${formatTimeShort(t.period_end)})`}
                              </>
                            : "ยังไม่ระบุ"}
                        </div>
                      )
                    : "-"}
                </td>
                <td className="text-center">{item.sub_semester}</td>
                <td className="text-center">{item.advisor_name}</td>
                <td className="text-center">
                  <div className="d-inline-flex gap-1">
                    <Link to={`/teaching/score-table/${item.teach_id}`} className="btn btn-outline-success btn-sm">
                      <i className="ri-table-line"></i> ตารางคะแนน
                    </Link>
                    <Link to={`/teaching/grade-entry/${item.teach_id}`} className="btn btn-primary btn-sm">
                      <i className="ri-edit-box-line"></i> บันทึกผลการเรียน
                    </Link>
                    <Dropdown>
                      <Dropdown.Toggle variant="outline-secondary" size="sm" id={`dropdown-por5-${item.teach_id}`}>
                        ปพ.5
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item as={Link} to={`/teaching/por5-assessment/${item.teach_id}`}>
                          <i className="ri-survey-line me-1"></i>ประเมินคุณลักษณะฯ / อ่านคิดวิเคราะห์
                        </Dropdown.Item>
                        <Dropdown.Item as={Link} to={`/teaching/por5-book/${item.teach_id}`}>
                          <i className="ri-printer-line me-1"></i>พิมพ์เล่ม ปพ.5
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                </td>
              </tr>
            ))
        ) : (
          <tr>
            <td colSpan={7} className="text-center">ไม่พบข้อมูล</td>
          </tr>
        )}
      </tbody>
    </>
  );

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="#">ข้อมูลการสอน</Link></li>
              <li className="breadcrumb-item active" aria-current="page">บันทึกผลการเรียน</li>
            </ol>
            <h4 className="main-title mb-0">บันทึกผลการเรียน</h4>
          </div>
          <div className="d-flex align-items-center gap-2 mt-3 mt-md-0">
            <Dropdown>
              <Dropdown.Toggle variant="primary" id="dropdown-year">
                {selectedYear || "เลือกปีการศึกษา"}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {yearList.map(item => (
                  <Dropdown.Item key={item.year_id} onClick={() => handleSelectYear(item.year_id)}>
                    ปีการศึกษา {item.year_year}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>

        <Row className="g-12 mb-4">
          <Col xl="12">
            <Card className="card-one">
              <Card.Header className="d-flex align-items-center justify-content-between gap-2">
                <Card.Title as="h6" className="mb-0">{selectedYear}</Card.Title>
                <div className="btn-group btn-group-sm" role="group" aria-label="สลับมุมมอง">
                  <button type="button" className={`btn btn${view === 'table' ? '' : '-outline'}-primary`}
                    onClick={() => setView('table')} title="มุมมองตาราง">
                    <i className="ri-table-line" />
                  </button>
                  <button type="button" className={`btn btn${view === 'card' ? '' : '-outline'}-primary`}
                    onClick={() => setView('card')} title="มุมมองการ์ด">
                    <i className="ri-layout-grid-line" />
                  </button>
                </div>
              </Card.Header>
              <Card.Body className="p-3">
                {view === 'card' ? (
                  <>
                    {semesterOrder.map((sem) => (
                      <React.Fragment key={sem}>{subjectCardSection(sem)}</React.Fragment>
                    ))}
                  </>
                ) : (
                <Table responsive hover className="mb-0">
                  {semesterOrder.map((sem) => (
                    <React.Fragment key={sem}>{subjectTableSection(sem)}</React.Fragment>
                  ))}
                </Table>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <Footer />
      </div>
    </React.Fragment>
  );
}
